import Phaser from 'phaser';
import { loadProfile, saveProfile, PersistentProfile, resetProfile, exportProfileBase64, importProfileBase64 } from '../game/persistence';
import { getItem } from '@neon-maw/shared';
import { apiClient } from '../game/apiClient';

export class HubScene extends Phaser.Scene {
  private profile!: PersistentProfile;
  private dailySeed: number = 0;

  constructor() { super('hub'); }

  create(): void {
    this.profile = loadProfile();
    // Start async tasks
    apiClient.syncProfile(this.profile);
    apiClient.flushQueues();
    
    let btnDaily: Phaser.GameObjects.Text;
    
    apiClient.getDailySeed().then(seed => {
      this.dailySeed = seed;
      if (btnDaily) {
        btnDaily.setText(`> DAILY RUN (${seed}) <`);
      }
    });

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('seed')) {
      this.startRun(parseInt(urlParams.get('seed')!));
      return;
    }
    
    // Background
    this.add.rectangle(0, 0, this.scale.width, this.scale.height, 0x111122).setOrigin(0, 0);

    const cx = this.scale.width / 2;
    
    this.add.text(cx, 60, 'NEON MAW', {
      fontFamily: 'monospace',
      fontSize: '64px',
      color: '#00ffff',
      fontStyle: 'bold'
    }).setOrigin(0.5);

    this.add.text(cx, 130, `Runs: ${this.profile.totalRuns} | High Score: ${this.profile.highScore} | Credits: ${this.profile.totalScore}`, {
      fontFamily: 'monospace',
      fontSize: '24px',
      color: '#fff'
    }).setOrigin(0.5);

    // Contracts
    this.add.text(cx, 200, 'Select Contract', { fontFamily: 'monospace', fontSize: '32px', color: '#ff00ff' }).setOrigin(0.5);

    const btnStandard = this.add.text(cx, 250, '> START STANDARD RUN <', {
      fontFamily: 'monospace', fontSize: '24px', color: '#00ff00', backgroundColor: '#004400'
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });

    btnStandard.on('pointerdown', () => {
      this.startRun(1); // diff 1 or seed 1
    });

    const btnHard = this.add.text(cx, 300, '> START HARD RUN <', {
      fontFamily: 'monospace', fontSize: '24px', color: '#ff0000', backgroundColor: '#440000'
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });

    btnHard.on('pointerdown', () => {
      this.startRun(5); // diff 5 (boss level)
    });

    btnDaily = this.add.text(cx, 350, `> DAILY RUN (...) <`, {
      fontFamily: 'monospace', fontSize: '24px', color: '#ffff00', backgroundColor: '#444400'
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });

    btnDaily.on('pointerdown', () => {
      this.startRun(this.dailySeed);
    });

    // Loadout
    this.add.text(cx, 420, 'Inventory Loadout', { fontFamily: 'monospace', fontSize: '32px', color: '#ffff00' }).setOrigin(0.5);

    let y = 440;
    this.profile.unlockedItems.forEach(id => {
      const it = getItem(id);
      if (!it) return;
      const isEquipped = this.profile.equippedItems.includes(id);
      const color = isEquipped ? '#00ffff' : '#888888';
      const text = `${isEquipped ? '[x]' : '[ ]'} ${it.name}`;
      
      const itemBtn = this.add.text(cx, y, text, {
        fontFamily: 'monospace', fontSize: '20px', color
      }).setOrigin(0.5).setInteractive({ useHandCursor: true });
      
      itemBtn.on('pointerdown', () => {
        if (isEquipped) {
          this.profile.equippedItems = this.profile.equippedItems.filter(i => i !== id);
        } else {
          if (this.profile.equippedItems.length < 3) {
            this.profile.equippedItems.push(id);
          }
        }
        saveProfile(this.profile);
        this.scene.restart();
      });
      y += 30;
    });

    // Save Management
    const saveY = this.scale.height - 50;
    
    const btnExport = this.add.text(cx - 150, saveY, '[ EXPORT ]', {
      fontFamily: 'monospace', fontSize: '18px', color: '#aaa', backgroundColor: '#333'
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    
    btnExport.on('pointerdown', () => {
      const b64 = exportProfileBase64();
      prompt('Copy your save data:', b64);
    });

    const btnImport = this.add.text(cx, saveY, '[ IMPORT ]', {
      fontFamily: 'monospace', fontSize: '18px', color: '#aaa', backgroundColor: '#333'
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });

    btnImport.on('pointerdown', () => {
      const b64 = prompt('Paste your save data:');
      if (b64) {
        try {
          importProfileBase64(b64);
          alert('Save imported successfully!');
          this.scene.restart();
        } catch (e) {
          alert('Failed to import save data.');
        }
      }
    });

    const btnReset = this.add.text(cx + 150, saveY, '[ RESET ]', {
      fontFamily: 'monospace', fontSize: '18px', color: '#f55', backgroundColor: '#333'
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });

    btnReset.on('pointerdown', () => {
      if (confirm('Are you sure you want to completely wipe your save?')) {
        resetProfile();
        this.scene.restart();
      }
    });
  }

  private async startRun(seed: number) {
    this.profile.totalRuns++;
    saveProfile(this.profile);
    const sessionId = await apiClient.startSession();
    this.scene.start('main', { seed, inventory: this.profile.equippedItems, sessionId });
  }
}